import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Route, Routes } from "react-router-dom";
import { AdminGuard } from "./components/AuthComponents/AdminGuard";
import { tbsRoutes } from "./routes/tbsRoutes";
import usePageTracking from "./hooks/usePageTracking";
import useAnalyticsUser from "./hooks/useAnalyticsUser";

import PageLoader from "./components/PageLoader/PageLoader";
import NotFoundPage from "./pages/NotFoundPage/NotFoundPage";
import DailyBudgetPage from "./pages/DailyBudgetPage/DailyBudgetPage";
import HomePage from "./pages/HomePage/HomePage";
import ProcessOrderPage from "./pages/ProcessOrderPage/ProcessOrderPage";
import SecurityCameraInstallingPage from "./pages/SecurityCameraInstallingPage/SecurityCameraInstallingPage";
import ProductDetailPage from "./pages/ProductDetailPage/ProductDetailPage";
import MobilePhoneRepairing from "./pages/MobilePhoneRepairing/MobilePhoneRepairing";
import ComputerRepairing from "./pages/ComputerRepairingPage/ComputerRepairingPage";
import OrderPage from "./pages/OrderPage/OrderPage";
import LocationPage from "./pages/LocationPage/LocationPage";
import RedirectToGames from "./pages/GamesPage/GamesPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage/PrivacyPolicyPage";
import ConditionsPage from "./pages/ConditionsPage/ConditionsPage";
import ItemMaintenancePage from "./pages/ItemMaintenancePage/ItemMaintenancePage";
import CreateItemPage from "./pages/ItemMaintenancePage/CreateItemPage";
import ItemListPage from "./pages/ItemMaintenancePage/ItemListPage";
import EducationPage from "./pages/EducationPage/EducationPage";
import EducationDetailPage from "./pages/EducationPage/EducationDetailPage";
import BannerListPage from "./pages/BannerMaintenancePage/BannerListPage";
import CreateBannerPage from "./pages/BannerMaintenancePage/CreateBannerPage";
import BannerEditPage from "./pages/BannerMaintenancePage/BannerEditPage";
import EducationItemListPage from "./pages/EducationItemMaintenancePage/EducationItemListPage";
import CreateEducationItemPage from "./pages/EducationItemMaintenancePage/CreateEducationItemPage";
import EducationItemEditPage from "./pages/EducationItemMaintenancePage/EducationItemEditPage";
import AlegraItemsPage from "./pages/AlegraItemsPage/AlegraItemsPage";
import DeliveryZoneListPage from "./pages/DeliveryMaintenancePage/DeliveryZoneListPage";
import { CartDrawerProvider } from "./components/CartComponent/CartDrawerProvider";
import CookieBanner from "./components/CookieConsent/CookieBanner";

export const App: React.FC = () => {
  const { isLoading } = useAuth0();

  // Order matters and is not incidental: both effects unblock on the same `isLoading` flip and
  // flush in the same commit, in call order, so the identity is set before the page view that
  // should carry it (EB-558).
  useAnalyticsUser();
  usePageTracking();

  if (isLoading) {
    return <PageLoader />;
  }
  return (
    <CartDrawerProvider>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/ubicacion" element={<LocationPage />} />
        <Route path="/reparacion-de-celulares" element={<MobilePhoneRepairing />} />
        <Route path="/reparacion-de-computadores" element={<ComputerRepairing />} />
        <Route
          path="/instalacion-de-camaras-de-seguridad"
          element={<SecurityCameraInstallingPage />}
        />
        <Route path="/producto/:id" element={<ProductDetailPage />} />
        <Route path="/item-maintenance/new" element={<AdminGuard component={CreateItemPage} />} />
        <Route
          path="/item-maintenance/:id"
          element={<AdminGuard component={ItemMaintenancePage} />}
        />
        <Route path="/item-maintenance" element={<AdminGuard component={ItemListPage} />} />
        <Route
          path="/banner-maintenance/new"
          element={<AdminGuard component={CreateBannerPage} />}
        />
        <Route path="/banner-maintenance/:id" element={<AdminGuard component={BannerEditPage} />} />
        <Route path="/banner-maintenance" element={<AdminGuard component={BannerListPage} />} />
        <Route
          path="/education-item-maintenance/new"
          element={<AdminGuard component={CreateEducationItemPage} />}
        />
        <Route
          path="/education-item-maintenance/:slug"
          element={<AdminGuard component={EducationItemEditPage} />}
        />
        <Route
          path="/education-item-maintenance"
          element={<AdminGuard component={EducationItemListPage} />}
        />
        <Route path="/alegra-items" element={<AdminGuard component={AlegraItemsPage} />} />
        <Route
          path="/delivery-maintenance"
          element={<AdminGuard component={DeliveryZoneListPage} />}
        />
        <Route path="/hacer-pedido" element={<ProcessOrderPage />} />
        <Route path="/pedido/:id" element={<OrderPage />} />
        <Route path="/politica-de-privacidad" element={<PrivacyPolicyPage />} />
        <Route path="/condiciones-del-servicio" element={<ConditionsPage />} />
        <Route path="/educacion/:slug" element={<EducationDetailPage />} />
        <Route path="/educacion" element={<EducationPage />} />
        <Route path="/contabilidad" element={<AdminGuard component={DailyBudgetPage} />} />
        <Route path="/games" element={<RedirectToGames />} />

        {/* Tiendas Biai. Returns null unless REACT_APP_TBS_ENABLED is set, so on every storefront
            deployment these routes do not exist and /gestion/* falls through to the 404 below. */}
        {tbsRoutes()}

        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      {/* Outside `Routes` so the prompt is not tied to a page: it has to be answerable from
          wherever the shopper landed, including a shared product link (EB-559). */}
      <CookieBanner />
    </CartDrawerProvider>
  );
};
